import React from "react";
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from "../../../components/ui/Alert";
import { Badge } from "../../../components/ui/Badge";
import { Shield, Calendar, CheckCircle, AlertCircle } from "lucide-react";
import {
    SubscriptionPlan,
    SubscriptionData,
} from "../../../contexts/AuthContext";
import { getSubscriptionStatusProperties } from "../../../utils/subscription";

interface CurrentSubscriptionProps {
    subscription?: SubscriptionData | null;
    subscriptionPlan?: SubscriptionPlan | null;
    hasActiveSubscription?: boolean;
}

const CurrentSubscription: React.FC<CurrentSubscriptionProps> = ({
    subscription,
    subscriptionPlan,
    hasActiveSubscription,
}) => {
    // Helper function to determine theme colors
    const getThemeColors = () => {
        const planName = subscriptionPlan?.name?.toLowerCase() || "";
        if (planName.includes("plus")) {
            return {
                icon: "text-green-500",
                badge: "bg-green-100 text-green-800",
                check: "text-green-500",
            };
        } else if (planName.includes("pro")) {
            return {
                icon: "text-orange-500",
                badge: "bg-orange-100 text-orange-800",
                check: "text-orange-500",
            };
        }
        return {
            icon: "text-purple-500",
            badge: "bg-purple-100 text-purple-800",
            check: "text-purple-500",
        };
    };

    const themeColors = getThemeColors();

    // For users with no subscription, show the free plan status
    if (!subscription) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                <div className="flex items-start">
                    <Shield
                        className={`h-6 w-6 ${themeColors.icon} mt-0.5 mr-4 flex-shrink-0`}
                    />
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Current Subscription: Basic (Free)
                            </h3>
                            <Badge className={themeColors.badge}>Active</Badge>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center">
                                <CheckCircle
                                    className={`h-5 w-5 ${themeColors.check} mr-2`}
                                />
                                <span className="text-gray-600">
                                    Basic plan is always active
                                </span>
                            </div>
                            <div className="flex items-center">
                                <AlertCircle
                                    className={`h-5 w-5 ${themeColors.icon} mr-2`}
                                />
                                <span className="text-gray-600">
                                    Limited to 5 mock interviews per month
                                </span>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                            Upgrade to a paid plan to access more features and
                            unlimited interviews.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const { color, text } = getSubscriptionStatusProperties(
        subscription.status as any
    );

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
            <div className="flex items-start">
                <Shield
                    className={`h-6 w-6 ${themeColors.icon} mt-0.5 mr-4 flex-shrink-0`}
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Current Subscription:{" "}
                            {subscriptionPlan?.name || "Unknown Plan"}
                        </h3>
                        <Badge className={themeColors.badge}>{text}</Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center">
                            {subscription.nextBillingTime ? (
                                <>
                                    <Calendar className="h-5 w-5 text-gray-500 mr-2" />
                                    <span className="text-gray-600">
                                        Next billing date:{" "}
                                        {new Date(
                                            subscription.nextBillingTime
                                        ).toLocaleDateString()}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <Calendar className="h-5 w-5 text-gray-500 mr-2" />
                                    <span className="text-gray-600">
                                        No upcoming billing
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="flex items-center">
                            {hasActiveSubscription ? (
                                <>
                                    <CheckCircle
                                        className={`h-5 w-5 ${themeColors.check} mr-2`}
                                    />
                                    <span className="text-gray-600">
                                        Your subscription is active
                                    </span>
                                </>
                            ) : (
                                <>
                                    <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                                    <span className="text-gray-600">
                                        Your subscription is not active
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    {!hasActiveSubscription && (
                        <p className="text-sm text-gray-500 mt-2">
                            Please renew or choose a new plan below to access
                            premium features.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CurrentSubscription;
